import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Global ErrorBoundary — catches rendering errors (e.g. React error #130 after
 * a stale session goes blank on idle). Instead of a white page we show a
 * friendly recovery card with "Refresh / Sign in again".
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for ops — production users can ignore
    if (typeof window !== 'undefined' && window.console) {
      console.error('[ErrorBoundary]', error, info?.componentStack);
    }
    // Fire-and-forget: POST to /api/admin/client-errors so admins see the
    // crash in Admin → System Health → Recent Errors without needing the
    // user to send us their console log.
    this.reportError(error, info);
  }

  async reportError(error, info) {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const payload = {
        message: (error?.message || String(error) || 'Unknown error').slice(0, 2000),
        stack: (error?.stack || '').slice(0, 20000),
        component_stack: (info?.componentStack || '').slice(0, 20000),
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        tab_name: this.props.tabName || 'App',
      };
      await fetch(`${base}/api/admin/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
    } catch { /* reporting must not cascade */ }
  }

  handleReload = () => {
    // Clear any cached auth state so a stale session doesn't loop us right
    // back into the same broken screen. Also clears the mirrored session
    // token in localStorage so cross-tab listeners transition cleanly.
    try {
      sessionStorage.clear();
      localStorage.removeItem('harmonix_user');
      localStorage.removeItem('harmonix_feature_access');
      localStorage.removeItem('session_token');
    } catch { /* ignore */ }
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full border-amber-200">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">Something went wrong</h2>
            <p className="text-sm text-slate-600">
              We hit an unexpected error. Refresh the page to keep going.
            </p>
            <Button
              onClick={this.handleReload}
              className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 gap-2"
              data-testid="error-boundary-reload"
            >
              <RefreshCw className="w-4 h-4" /> Refresh page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
